import * as React from 'react'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useLocation } from 'react-router-dom'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import type { TimesheetRowData } from './Group.types'
import { normalizeMonthKey, formatNumber, formatDate } from './Group.utils'
import useDriverService from '@/modules/drivers/services/DriverService'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'

export const useMonthSync = (
  selectedMonthKey: string,
  setSelectedMonthKey: (value: string) => void,
) => {
  const { month: dateContextMonth, setMonth: setDateContextMonth } = useDate()
  const lastSyncedDateContextRef = React.useRef<string | null>(null)
  const lastSyncedSelectedMonthRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    const normalized = normalizeMonthKey(selectedMonthKey)
    if (normalized !== selectedMonthKey) {
      
      setSelectedMonthKey(normalized)
    }
  }, [selectedMonthKey, setSelectedMonthKey])

  React.useEffect(() => {
    if (!dateContextMonth) {
      return
    }

    if (lastSyncedDateContextRef.current === dateContextMonth) {
     
      return
    }

    const parts = dateContextMonth.split('-')
    if (parts.length !== 2) {
   
      return
    }

    const year = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10)

    if (isNaN(year) || isNaN(month) || month < 1 || month > 12) {
     
      return
    }

    const selectedMonthFormat = `${String(month).padStart(2, '0')}-${year}`
    const normalized = normalizeMonthKey(selectedMonthFormat)
    const currentNormalized = normalizeMonthKey(selectedMonthKey)

   

    if (currentNormalized !== normalized) {
     
      lastSyncedDateContextRef.current = dateContextMonth
      lastSyncedSelectedMonthRef.current = normalized
      setSelectedMonthKey(normalized)
    } else {
      lastSyncedDateContextRef.current = dateContextMonth
    }
  }, [dateContextMonth, selectedMonthKey, setSelectedMonthKey])

  // Sync selectedMonthKey -> DateContext (MM-YYYY -> YYYY-MM) - only if DateContext hasn't changed
  React.useEffect(() => {
    if (!selectedMonthKey) {
      return
    }

    // Skip if we just synced this value
    if (lastSyncedSelectedMonthRef.current === selectedMonthKey) {
     
      return
    }

    // Skip if DateContext was just updated (user changed picker)
    if (
      lastSyncedDateContextRef.current === dateContextMonth &&
      dateContextMonth !== null
    ) {
     
      return
    }

    const normalized = normalizeMonthKey(selectedMonthKey)
    const [monthStr, yearStr] = normalized.split('-')
    const month = parseInt(monthStr, 10)
    const year = parseInt(yearStr, 10)

    if (isNaN(month) || isNaN(year) || month < 1 || month > 12) {
    
      return
    }

    const dateContextFormat = `${year}-${String(month).padStart(2, '0')}`
  

    if (dateContextMonth !== dateContextFormat) {
      
      lastSyncedSelectedMonthRef.current = normalized
      lastSyncedDateContextRef.current = dateContextFormat
      setDateContextMonth(dateContextFormat)
    } else {
      lastSyncedSelectedMonthRef.current = normalized
    }
  }, [selectedMonthKey, dateContextMonth, setDateContextMonth])
}

const INCREMENTAL_DEBOUNCE_MS = 300

export const useGroupedTimesheets = (
  companyId: string,
  selectedMonthKey: string,
  showBonusFields: boolean,
  sideCheckedIds: number[],
) => {
  const { refreshState } = useDrivers()
  const location = useLocation()
  const locationKey = location.key
  const { getDriversPoor } = useDriverService()
  const { getGroupedTimesheets } = useTimesheetService()
  const startLoading = useLoadingStore((s) => s.startLoading)

  const [drivers, setDrivers] = React.useState<any[]>([])
  React.useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const driversData = await getDriversPoor({ companyId })
        setDrivers(driversData.filter((d: any) => d.isActive))
      } catch (error) {
        console.error('Error fetching drivers:', error)
        setDrivers([])
      }
    }
    fetchDrivers()
  }, [companyId])

  const driverById = React.useMemo(() => {
    const map = new Map<number, any>()
    drivers.forEach((d) => map.set(d.id, d))
    return map
  }, [drivers])

  const [data, setData] = React.useState<TimesheetRowData[]>([])

  const fetchedIdsRef = React.useRef<Set<number>>(new Set())
  const debounceRef = React.useRef<any>(null)
  const pendingStopRef = React.useRef<null | (() => void)>(null)
  const sideCheckedIdsRef = React.useRef<number[]>(sideCheckedIds)
  sideCheckedIdsRef.current = sideCheckedIds

  const defaultRow = React.useCallback(
    (driverId: number): TimesheetRowData => {
      const driver = driverById.get(driverId)
      return {
        id: String(driverId),
        driverName: driver
          ? driver.fullName || `${driver.surname} ${driver.name}`
          : String(driverId),
        name: driver?.name ?? '',
        surname: driver?.surname ?? '',
        calculations: Array(12).fill(false),
        hasContract: Array(12).fill(3),
        costs: '0,00',
        grossTotal: '0,00',
        lastCalculationDate: '',
      }
    },
    [driverById],
  )

  const mapRow = React.useCallback((groupedTimesheet: any): TimesheetRowData => {
    const driver = groupedTimesheet.driver
    const driverName = driver.fullName || `${driver.surname} ${driver.name}`
    const calculations = groupedTimesheet.calculations || Array(12).fill(false)
    const hasContract = groupedTimesheet.hasContract || Array(12).fill(3)
    const grossTotal = formatNumber(groupedTimesheet.summary)
    const lastCalculationDate = formatDate(groupedTimesheet.lastCalculationDate)

    const vacationNb =
      groupedTimesheet.leavesNettoAmount != null &&
      groupedTimesheet.leavesGrossAmount != null
        ? `${formatNumber(groupedTimesheet.leavesNettoAmount)} / ${formatNumber(groupedTimesheet.leavesGrossAmount)}`
        : undefined

    const sickLeaveNb =
      groupedTimesheet.sickLeavesNettoAmount != null &&
      groupedTimesheet.sickLeavesGrossAmount != null
        ? `${formatNumber(groupedTimesheet.sickLeavesNettoAmount)}/${formatNumber(groupedTimesheet.sickLeavesGrossAmount)}`
        : undefined

    return {
      id: String(driver.id),
      driverName,
      name: driver.name,
      surname: driver.surname,
      calculations,
      hasContract,
      // Ta sama logika co w gridzie "premia i nagroda" (PayrollResultsTableRow) - obie listy
      // musza pokazywac te sama kwote. Wczesniej byl tu literal '0,00'.
      costs: formatNumber(
        groupedTimesheet.tripCosts ?? groupedTimesheet.additionAmount,
      ),
      domesticDelegations: formatNumber(groupedTimesheet.polishTripsAmount),
      grossTotal,
      lastCalculationDate,
      vacationNb,
      sickLeaveNb,
      expectedNetto:
        groupedTimesheet.expectedNettoAmount != null
          ? formatNumber(groupedTimesheet.expectedNettoAmount)
          : undefined,
      calculatedNetto:
        groupedTimesheet.calculatedNetto != null
          ? formatNumber(groupedTimesheet.calculatedNetto)
          : undefined,
      reward:
        groupedTimesheet.reward != null
          ? formatNumber(groupedTimesheet.reward)
          : undefined,
      grossTotal7Day:
        groupedTimesheet.summary != null
          ? formatNumber(groupedTimesheet.summary)
          : undefined,
    }
  }, [])

  const parseMonth = React.useCallback(() => {
    const normalizedMonthKey = normalizeMonthKey(selectedMonthKey)
    if (!normalizedMonthKey || typeof normalizedMonthKey !== 'string') {
      return null
    }
    const [monthStr, yearStr] = normalizedMonthKey.split('-')
    const month = parseInt(monthStr, 10)
    const year = parseInt(yearStr, 10)
    if (isNaN(month) || isNaN(year) || month < 1 || month > 12) return null
    return { month, year }
  }, [selectedMonthKey])

  const fetchForIds = React.useCallback(
    async (ids: number[], mode: 'replace' | 'merge') => {
      const parsed = parseMonth()
      if (!parsed) return

      if (mode === 'replace') {
        fetchedIdsRef.current = new Set(ids)
      } else {
        ids.forEach((id) => fetchedIdsRef.current.add(id))
      }

      if (ids.length === 0) {
        if (mode === 'replace') setData([])
        return
      }

      const upsert = (rows: TimesheetRowData[]) => {
        if (mode === 'replace') {
          setData(rows)
          return
        }
        setData((prev) => {
          const map = new Map(prev.map((r) => [r.id, r]))
          rows.forEach((r) => map.set(r.id, r))
          return Array.from(map.values())
        })
      }

      try {
        const groupedTimesheets = await getGroupedTimesheets({
          companyId,
          driversIds: ids.map(String),
          year: parsed.year,
          month: parsed.month,
          // This list renders only name / calculations / hasContract / summary /
          // lastCalculationDate; asking for the bonus configuration made the server
          // load full timesheet models + per-driver bonus config for every row.
          showBonusConfiguration: false,
        })
        const rowsById = new Map<string, TimesheetRowData>(
          (groupedTimesheets || []).map((g: any) => {
            const row = mapRow(g)
            return [row.id, row] as [string, TimesheetRowData]
          }),
        )
        upsert(ids.map((id) => rowsById.get(String(id)) ?? defaultRow(id)))
      } catch (error) {
        console.error('Error fetching grouped timesheets:', error)
        upsert(ids.map((id) => defaultRow(id)))
      }
    },
    [companyId, parseMonth, getGroupedTimesheets, mapRow, defaultRow],
  )

  const fetchForIdsRef = React.useRef(fetchForIds)
  fetchForIdsRef.current = fetchForIds

  const fetchGroupedTimesheets = React.useCallback(async () => {
    await fetchForIdsRef.current(sideCheckedIdsRef.current, 'replace')
  }, [])

  // Refresh a single row through the same lean fetch + mapping as the grid load.
  const refreshRows = React.useCallback(async (ids: number[]) => {
    await fetchForIdsRef.current(ids, 'merge')
  }, [])

  React.useEffect(() => {
    fetchForIdsRef.current(sideCheckedIdsRef.current, 'replace')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedMonthKey, showBonusFields, refreshState, locationKey])


  React.useEffect(() => {
    const newIds = sideCheckedIds.filter((id) => !fetchedIdsRef.current.has(id))

    if (newIds.length === 0) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      if (pendingStopRef.current) {
        pendingStopRef.current()
        pendingStopRef.current = null
      }
      return
    }

    if (!pendingStopRef.current) {
      pendingStopRef.current = startLoading()
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      const ids = sideCheckedIdsRef.current.filter(
        (id) => !fetchedIdsRef.current.has(id),
      )
      const stop = pendingStopRef.current
      pendingStopRef.current = null
      Promise.resolve(fetchForIdsRef.current(ids, 'merge')).finally(() => {
        if (stop) stop()
      })
    }, INCREMENTAL_DEBOUNCE_MS)
  }, [sideCheckedIds, startLoading])

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (pendingStopRef.current) {
        pendingStopRef.current()
        pendingStopRef.current = null
      }
    }
  }, [])

  return { data, setData, fetchGroupedTimesheets, refreshRows }
}
